import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Detecta APENAS browsers in-app (WhatsApp, Instagram, etc.) onde popup E redirect falham.
// Chrome Android e Safari iOS normais NÃO são in-app — popup funciona neles.
const isInAppBrowser = () => {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|Instagram|Line|Snapchat|TikTok|Pinterest|Twitter\/|wv\b/i.test(ua);
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GIRAFFE_MASCOT = '/images/simby-mascote.png';

// ── Firebase error messages in PT ──────────────────────────────────────────
const FB_ERRORS = {
  'auth/user-not-found':           'E-mail não encontrado.',
  'auth/wrong-password':           'Senha incorreta.',
  'auth/invalid-credential':       'E-mail ou senha incorretos.',
  'auth/invalid-email':            'E-mail inválido.',
  'auth/too-many-requests':        'Muitas tentativas. Aguarde e tente novamente.',
  'auth/network-request-failed':   'Erro de conexão. Verifique sua internet.',
  'auth/popup-closed-by-user':     'Login cancelado.',
  'auth/operation-not-allowed':    'Login com email/senha não está habilitado. Contate o administrador.',
  'auth/email-already-in-use':     'E-mail já cadastrado.',
  'auth/weak-password':            'Senha muito fraca. Use pelo menos 6 caracteres.',
};
const fbMsg = (code) => FB_ERRORS[code] || `Erro ao entrar (${code || 'desconhecido'}). Tente novamente.`;

// ── Shared: exchange Firebase ID token for backend session ─────────────────
// rememberMe controla a duração do cookie no backend (90 dias se true, 7 se false).
const exchangeToken = async (firebaseUser, rememberMe = true) => {
  const idToken = await firebaseUser.getIdToken();
  const res = await axios.post(
    `${API}/auth/firebase-session`,
    {
      idToken,
      email:   firebaseUser.email,
      name:    firebaseUser.displayName || firebaseUser.email.split('@')[0],
      picture: firebaseUser.photoURL || null,
      rememberMe,
    },
    { withCredentials: true }
  );
  // Guarda o session token como Bearer fallback para quando cookies de terceiros
  // são bloqueados pelo browser (incógnito, configurações de privacidade, etc.)
  const { _sessionToken, ...userData } = res.data || {};
  if (_sessionToken) {
    localStorage.setItem('sb_session', _sessionToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${_sessionToken}`;
  }
  return userData;
};

// ── Tenta ativar plano teste com cupom 100% off após cadastro ──────────────
// Retorna true se a assinatura foi ativada (cupom válido + 100% off),
// false caso contrário. Nunca lança — falha graciosa para não bloquear cadastro.
const tryActivateCoupon = async (couponCode) => {
  const code = (couponCode || '').trim().toUpperCase();
  if (!code) return false;
  try {
    await axios.post(
      `${API}/subscriptions/activate-with-coupon`,
      { coupon_code: code, plan_id: 'monthly' },
      { withCredentials: true }
    );
    return true;
  } catch {
    return false;
  }
};

// ── Main component ─────────────────────────────────────────────────────────
const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [mode, setMode]         = useState('login');   // 'login' | 'register' | 'reset'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [couponCode, setCouponCode] = useState(''); // opcional no cadastro — ativa plano teste se 100% off
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]       = useState('');
  const [info, setInfo]         = useState(''); // mensagens informativas (não-erro), ex: cupom não confere
  const [resetSent, setResetSent]     = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // marcado por padrão — sessão de 90 dias

  // ── Navigate after session established ──────────────────────────────────
  // login() é async (aguarda checkSubscription). Precisa ser awaited para que
  // SubscriptionRequired não veja hasSubscription=false ao renderizar /dashboard.
  const afterLogin = async (userData) => {
    await login(userData);
    // Onboarding enxuto (Simby-3): usuário novo vê o vídeo de boas-vindas uma vez;
    // depois entra direto no dashboard. Perfil Relacional/PDI ficam opcionais na
    // aba "Plano de Desenvolvimento" — não bloqueiam mais a entrada.
    navigate(userData.hasCompletedOnboarding ? '/dashboard' : '/onboarding', { replace: true });
  };

  // ── Email + password login ───────────────────────────────────────────────
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!consentChecked) { setError('Aceite os termos para continuar.'); return; }
    setIsLoading(true);
    setError(''); setInfo('');
    try {
      const result  = await signInWithEmailAndPassword(auth, email.trim(), password);
      const session = await exchangeToken(result.user, rememberMe);
      afterLogin(session);
    } catch (err) {
      setError(fbMsg(err.code));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Register ─────────────────────────────────────────────────────────────
  // Se um código de cupom for informado e ele zerar o valor (100% off),
  // ativamos o plano teste imediatamente e o usuário cai no /dashboard.
  // Cupons inválidos ou parciais não bloqueiam o cadastro — usuário segue para
  // o paywall onde pode retentar o cupom no checkout pago.
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!consentChecked) { setError('Aceite os termos para continuar.'); return; }
    if (!name.trim()) { setError('Informe seu nome.'); return; }
    setIsLoading(true);
    setError(''); setInfo('');
    try {
      const result = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await updateProfile(result.user, { displayName: name.trim() });
      const session = await exchangeToken(result.user, rememberMe);
      // Tenta ativar cupom (silencioso se falhar)
      if (couponCode.trim()) {
        const activated = await tryActivateCoupon(couponCode);
        if (activated) {
          // Recarrega a página para que a sessão reconheça a assinatura ativada
          login(session);
          window.location.href = session.hasCompletedOnboarding ? '/dashboard' : '/onboarding';
          return;
        } else {
          setInfo('Cupom não confere ou não é gratuito — você pode tentar novamente no checkout.');
        }
      }
      afterLogin(session);
    } catch (err) {
      setError(fbMsg(err.code));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Google login ─────────────────────────────────────────────────────────
  const handleGoogleLogin = async () => {
    if (!consentChecked) { setError('Aceite os termos para continuar.'); return; }
    setIsLoading(true);
    setError(''); setInfo('');
    try {
      const result  = await signInWithPopup(auth, googleProvider);
      const session = await exchangeToken(result.user, rememberMe);
      // Se for primeiro acesso via Google e usuário tiver cupom, tentar ativar.
      if (mode === 'register' && couponCode.trim()) {
        const activated = await tryActivateCoupon(couponCode);
        if (!activated) {
          setInfo('Cupom não confere ou não é gratuito — você pode tentar novamente no checkout.');
        }
      }
      afterLogin(session);
    } catch (err) {
      setError(fbMsg(err.code));
    } finally {
      setIsLoading(false);
    }
  };

  // ── Password reset ───────────────────────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setResetSent(true);
    } catch (err) {
      setError(fbMsg(err.code));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">

        {/* Logo */}
        <div className="text-center mb-6">
          <img src={GIRAFFE_MASCOT} alt="Simby" className="w-24 h-36 object-contain mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">
            {mode === 'login'    && 'Entrar na Simby'}
            {mode === 'register' && 'Criar conta'}
            {mode === 'reset'    && 'Recuperar senha'}
          </h1>
          {mode === 'login' && (
            <p className="text-gray-500 text-sm mt-1">Sua jornada relacional continua aqui</p>
          )}
          {mode === 'register' && (
            <p className="text-gray-500 text-sm mt-1">Comece sua jornada de inteligência relacional</p>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Info (não-erro, ex: cupom não confere) */}
        {info && !error && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            {info}
          </div>
        )}

        {/* ── Password reset mode ── */}
        {mode === 'reset' && (
          <>
            {resetSent ? (
              <div className="text-center">
                <p className="text-green-700 bg-green-50 border border-green-200 rounded-lg p-4 text-sm mb-5">
                  Link de recuperação enviado para <strong>{email}</strong>. Verifique sua caixa de entrada.
                </p>
                <button
                  onClick={() => { setMode('login'); setResetSent(false); }}
                  className="text-purple-600 text-sm hover:underline"
                >
                  Voltar para o login
                </button>
              </div>
            ) : (
              <form onSubmit={handleReset} className="space-y-4">
                <p className="text-sm text-gray-600">
                  Informe o e-mail da sua conta. Enviaremos um link para redefinir sua senha.
                </p>
                <input
                  type="email"
                  required
                  placeholder="seu@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
                <Button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {isLoading ? 'Enviando...' : 'Enviar link'}
                </Button>
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="w-full text-sm text-gray-500 hover:text-gray-700"
                >
                  Voltar para o login
                </button>
              </form>
            )}
          </>
        )}

        {/* ── Register mode ── */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} className="space-y-3 mb-4">
            <input
              type="text"
              required
              placeholder="Seu nome completo"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <input
              type="email"
              required
              placeholder="E-mail"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <input
              type="password"
              required
              minLength={6}
              placeholder="Senha (mínimo 6 caracteres)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <input
              type="text"
              placeholder="Código de acesso (se você recebeu um)"
              value={couponCode}
              onChange={e => setCouponCode(e.target.value.toUpperCase())}
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 uppercase"
              autoComplete="off"
            />
            <p className="text-xs text-gray-400 -mt-2">
              Recebeu um código de acesso por indicação? Informe aqui e entre direto — sem pagamento.
            </p>
            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="remember-me-reg"
                checked={rememberMe}
                onCheckedChange={setRememberMe}
                className="mt-0.5"
              />
              <label htmlFor="remember-me-reg" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
                Lembrar de mim neste dispositivo (90 dias)
              </label>
            </div>
            <div className="flex items-start gap-3 pt-1">
              <Checkbox
                id="terms-consent-reg"
                checked={consentChecked}
                onCheckedChange={setConsentChecked}
                className="mt-0.5"
              />
              <label htmlFor="terms-consent-reg" className="text-xs text-gray-500 cursor-pointer leading-relaxed">
                Li e concordo com os{' '}
                <Link to="/terms" className="text-purple-600 underline">Termos de Uso</Link>
                {' '}e a{' '}
                <Link to="/privacy" className="text-purple-600 underline">Política de Privacidade</Link>
              </label>
            </div>
            <Button
              type="submit"
              disabled={isLoading || !consentChecked}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Criando conta...
                </span>
              ) : 'Criar conta'}
            </Button>
            <p className="text-xs text-gray-500 text-center mt-1">
              Já tem conta?{' '}
              <button type="button" onClick={() => { setMode('login'); setError(''); }}
                className="text-purple-600 hover:underline font-medium">
                Entrar
              </button>
            </p>
          </form>
        )}

        {/* ── Login mode ── */}
        {mode === 'login' && (
          <>
            {/* Email/password form */}
            <form onSubmit={handleEmailLogin} className="space-y-3 mb-4">
              <input
                type="email"
                required
                placeholder="E-mail"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
              />
              <div className="relative">
                <input
                  type="password"
                  required
                  placeholder="Senha"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>

              {/* Lembre-me (sessão de 90 dias quando marcado) */}
              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="mt-0.5"
                />
                <label htmlFor="remember-me" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
                  Lembrar de mim neste dispositivo (90 dias)
                </label>
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="terms-consent"
                  checked={consentChecked}
                  onCheckedChange={setConsentChecked}
                  className="mt-0.5"
                />
                <label htmlFor="terms-consent" className="text-xs text-gray-500 cursor-pointer leading-relaxed">
                  Li e concordo com os{' '}
                  <Link to="/terms" className="text-purple-600 underline">Termos de Uso</Link>
                  {' '}e a{' '}
                  <Link to="/privacy" className="text-purple-600 underline">Política de Privacidade</Link>
                </label>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !consentChecked}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Entrando...
                  </span>
                ) : 'Entrar'}
              </Button>
            </form>

            {/* Forgot password */}
            <div className="text-center mb-4">
              <button
                onClick={() => { setMode('reset'); setError(''); }}
                className="text-xs text-gray-400 hover:text-purple-600 hover:underline"
              >
                Esqueci minha senha
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">ou</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Google — escondido em browsers in-app onde popup e redirect falham */}
            {isInAppBrowser() ? (
              <div className="w-full p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs text-center leading-relaxed">
                Para entrar com Google, abra este link no <strong>Chrome</strong> ou <strong>Safari</strong>.<br />
                Ou use e-mail e senha acima.
              </div>
            ) : (
              <Button
                onClick={handleGoogleLogin}
                disabled={isLoading || !consentChecked}
                variant="outline"
                className="w-full border-2 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continuar com Google
              </Button>
            )}

            <p className="text-xs text-gray-500 text-center mt-4">
              Não tem conta?{' '}
              <button onClick={() => { setMode('register'); setError(''); }}
                className="text-purple-600 hover:underline font-medium">
                Cadastre-se
              </button>
            </p>

            <p className="text-xs text-gray-400 text-center mt-2">
              Seus dados são protegidos conforme a LGPD (Lei 13.709/2018)
            </p>
          </>
        )}
      </Card>
    </div>
  );
};

export default Login;
